import uuid from 'uuid';
import { getCloudinaryURL, getVersionedImageURLKeyFromParams } from '../images';

const buildUploadResult = uploadResponseData => {
  const resourceType = uploadResponseData.resource_type;
  const publicId = uploadResponseData.public_id;
  const format = uploadResponseData.format;
  const version = uploadResponseData.version;
  const size = uploadResponseData.bytes;
  const originalWidth = uploadResponseData.width;
  const originalHeight = uploadResponseData.height;
  const originalUrl = uploadResponseData.secure_url;

  const url = getCloudinaryURL(
    getVersionedImageURLKeyFromParams({
      publicId,
      format,
      version,
    }),
    { resource_type: resourceType },
    { width: originalWidth, height: originalHeight },
  );

  const uploadResult = {
    source: 'Cloudinary',
    url,
    originalUrl,
    publicId,
    format,
    version,
    size,
    originalWidth,
    originalHeight,
  };

  if (resourceType === 'video') {
    uploadResult.thumbnail = getCloudinaryURL(
      getVersionedImageURLKeyFromParams({
        publicId,
        format: 'jpg',
        version,
      }),
      { resource_type: resourceType },
    );
  }

  return uploadResult;
};

/**
 * Cloudinary upload implementation
 *
 * @param post: the post function to call (see index.js)
 * @param file: a blob or file object
 * @param uploadData: contains uploadUrl and uploadParams
 * @param progressCallback: the function to call with the progress value when it changes
 *
 * Returns a promise that will resolve with the url, the source, and optional additional params
 */
const upload = (post, file, uploadData, progressCallback) =>
  new Promise((resolve, reject) => {
    const { uploadUrl, uploadParams } = uploadData;
    const total = file.size;
    const step = 5 * 1024 * 1024;

    if (total > step) {
      const uploadId = uuid.v4();

      const sendChunk = (f, start, end) => {
        const blob = f.slice(start, end);
        const headers = {
          'Content-Range': `bytes ${start}-${end - 1}/${total}`,
          'X-Unique-Upload-Id': uploadId,
        };
        const onProgress = progress => {
          const totalProgress =
            start / total + progress * ((end - start) / total);
          progressCallback(totalProgress);
        };

        post(blob, uploadUrl, headers, uploadParams, onProgress)
          .then(responseData => {
            const newStart = start + step;
            progressCallback(end / total);

            if (newStart <= total) {
              sendChunk(f, newStart, Math.min(newStart + step, total));
            } else {
              resolve(buildUploadResult(responseData));
            }
          })
          .catch(reject);
      };

      sendChunk(file, 0, step);
    } else {
      const onProgress = progress => {
        progressCallback(progress);
      };

      post(file, uploadUrl, {}, uploadParams, onProgress)
        .then(responseData => {
          resolve(buildUploadResult(responseData));
        })
        .catch(reject);
    }
  });
export default upload;
